import { AfterViewInit, Component, ElementRef, inject, OnDestroy, ViewChild } from '@angular/core';
import { DecimalPipe } from '@angular/common';
import { RouterOutlet } from '@angular/router';
import { HttpClient } from '@angular/common/http';
import { catchError, firstValueFrom, of, Subscription } from 'rxjs';
import { ViewPort } from 'noodle/engine/context/ViewPort';
import { DOMContextBuilder } from 'noodle/engine/context/DOMContextBuilder';
import { AttachedCamera, Camera, FreeViewCamera } from 'noodle/engine/camera';
import { Engine } from 'noodle/engine/Engine';
import { InputHandler, MouseObjectInteraction } from 'noodle/engine/input';
import { Scene } from 'noodle/engine/scene/Scene';
import { SceneLoader } from 'noodle/engine/scene/SceneLoader';
import { SceneLoadProgressEvent } from 'noodle/engine/scene/SceneLoadProgress';
import { PlayerController, PointerLockProvider } from 'noodle/engine/player';
import { AmbientZoneController, RuntimeAudioResolver } from 'noodle/engine/audio';
import { sampleFootprintFloorHeight } from 'noodle/engine/spatial';
import { LoadingOverlay } from './loading-overlay/loading-overlay';
import { GraphicsSettingsPanel } from './graphics-settings/graphics-settings';
import { PerfOverlay } from './perf-overlay/perf-overlay';
import { CameraControls, createCameraActions, createPlayerActions } from './GameActions';
import { StressTestLights } from './StressTestLights';
import { DeferredSceneRenderer } from 'noodle/engine/renderer/DeferredSceneRenderer';
import { CompositeSceneRenderer } from 'noodle/engine/renderer/CompositeSceneRenderer';
import { RenderObject } from 'noodle/engine/object/RenderObject';
import { DayNightCycleFeature } from 'noodle/engine/scene/feature';
import { PhysicsFeature, PhysicsBody } from 'noodle/engine/physics';
import { GltfParser, resolveMaterialTextures, readModelTransform } from 'noodle/engine/gltf';
import { inflateGzip } from 'noodle/engine/util/gzip';
import { GeometryMesh, GltfMesh } from 'noodle/engine/mesh';
import { GltfAnimation } from 'noodle/engine/animation';
import { GltfAnimationDataTexture } from 'noodle/engine/texture';
import { createAnimationState } from 'noodle/engine/animation/AnimationStateFactory';
import { ParsedModel } from 'noodle/engine/scene/SceneManifestLoader';
import { SceneCompositeRef, SceneManifest } from 'noodle/engine/scene/SceneManifest';
import { manifestPlacementToEngine } from 'noodle/engine/composite/manifest';
import { composeAmbientSoundWorldPosition } from 'noodle/engine/composite/transform';
import { EntityMovementBehaviour } from 'noodle/engine/object/behaviours/EntityMovementBehaviour';
import { EntityWanderingBehaviour } from 'noodle/engine/object/behaviours/EntityWanderingBehaviour';
import { createCube, createSphere } from 'noodle/engine/geometry';
import { CompositeOverlay, MsdfFont, TextOverlay } from 'noodle/engine/overlay';
import { interRegular } from 'noodle/engine/overlay/fonts/inter-regular.generated';
import { BarOverlay } from './overlays/BarOverlay';
import { DamageNumberOverlay } from './overlays/DamageNumberOverlay';
import { vec3, vec4 } from 'gl-matrix';
import { TileParseWorkerClient, WorkerLike } from 'noodle/engine/workers/TileParseWorkerClient';

type EngineSettings = {
  keymap: Record<string, string[][]>;
};

@Component({
  selector: 'app-root',
  imports: [RouterOutlet, DecimalPipe, LoadingOverlay, GraphicsSettingsPanel, PerfOverlay],
  templateUrl: './app.html',
  styleUrl: './app.css',
})
export class App implements AfterViewInit, OnDestroy {
  private http = inject(HttpClient);

  @ViewChild('canvas', { static: true })
  canvas!: ElementRef<HTMLCanvasElement>;

  @ViewChild('textLayer', { static: true })
  textLayer!: ElementRef<HTMLDivElement>;

  cameras: Camera[] = [];
  scene!: Scene;
  devPanelCollapsed = false;
  graphicsDialogOpen = false;
  engine!: Engine;
  activeManifest = '';
  sceneList: { id: string; name: string; path: string }[] = [];

  loadingProgress: SceneLoadProgressEvent | null = null;

  stressTestLights?: StressTestLights;
  cameraControls?: CameraControls;
  private boxMesh?: GeometryMesh;
  physics?: PhysicsFeature;
  private playerObject?: RenderObject;
  private playerSetupDone = false;
  private ambientZoneController?: AmbientZoneController;
  debugBoneImpact = false;
  private inputHandler!: InputHandler;
  private loading = false;
  private sceneLoader!: SceneLoader;
  private progressSub?: Subscription;
  private tileParseWorker?: TileParseWorkerClient;
  private terrainGenWorker?: Worker;
  private interFontPromise?: Promise<MsdfFont>;
  private interFont?: MsdfFont;
  /** True after `ngOnDestroy` runs. Read by the font-load `.then()` to
   *  destroy the atlas if the component died while the load was in flight,
   *  closing the race between async decode and component teardown. */
  private destroyed = false;

  playerController!: PlayerController;

  get deferredRenderer(): DeferredSceneRenderer | undefined {
    const renderer = this.engine?.renderer;
    if (renderer instanceof CompositeSceneRenderer) {
      return renderer.deferredRenderer;
    }
    return undefined;
  }

  get dayNightCycle(): DayNightCycleFeature | undefined {
    return this.scene?.features.get('day-night-cycle') as DayNightCycleFeature | undefined;
  }

  setCycleSpeed(value: number) {
    const cycle = this.dayNightCycle;
    if (cycle) cycle.speed = Number(value);
  }

  setTimeOfDay(value: number) {
    const cycle = this.dayNightCycle;
    if (cycle) cycle.timeOfDay = Number(value);
  }

  toggleCycle(enabled: boolean) {
    const cycle = this.dayNightCycle;
    if (cycle) cycle.enabled = enabled;
  }

  setCameraSpeed(value: number) {
    if (this.cameraControls) this.cameraControls.moveSpeed = Number(value);
  }

  setStressLightCount(value: number) {
    this.stressTestLights?.setCount(Number(value));
  }

  spawnBox() {
    if (!this.boxMesh || !this.physics) return;

    const obj = this.scene.objectFactory.instantiateMesh(this.boxMesh, {
      position: [(Math.random() - 0.5) * 60, 20 + Math.random() * 20, (Math.random() - 0.5) * 30],
      color: vec4.fromValues(
        0.3 + Math.random() * 0.7,
        0.3 + Math.random() * 0.7,
        0.3 + Math.random() * 0.7,
        1,
      ),
    });

    this.physics.addBody(
      new PhysicsBody(obj.trs.position, { mode: 'gravity-only', surfaceOffset: 0.5 }),
    );
  }

  launchProjectile() {
    if (!this.physics) return;
    const obj = this.scene.objectFactory.instantiateModel('7f21b482-4fd7-5a18-9648-23d70916c4ec');
    if (!obj) return;

    const cam = this.engine.activeCamera;
    const camPos = cam.cameraPosition;
    const targetPos = cam.targetPosition;

    const forward = vec3.subtract(
      vec3.create(),
      vec3.fromValues(targetPos[0] as number, targetPos[1] as number, targetPos[2] as number),
      vec3.fromValues(camPos[0] as number, camPos[1] as number, camPos[2] as number),
    );
    vec3.normalize(forward, forward);

    const speed = 20 + Math.random() * 20;
    const velocity = vec3.scale(vec3.create(), forward, speed);
    velocity[1] += 5 + Math.random() * 15;

    const angularVelocity = vec3.fromValues(
      (Math.random() - 0.5) * 10,
      (Math.random() - 0.5) * 4,
      (Math.random() - 0.5) * 10,
    );

    this.physics.createProjectile({
      position: vec3.fromValues(camPos[0] as number, camPos[1] as number, camPos[2] as number),
      velocity,
      restitution: 0.15,
      mass: 5,
      maxBounces: 3,
      maxLifetime: 30,
      renderObject: obj,
      enableRotation: true,
      initialAngularVelocity: angularVelocity,
      angularDamping: 0.95,
      persist: true,
    });
  }

  launchArrow() {
    if (!this.physics) return;
    const obj = this.scene.objectFactory.instantiateModel('52384dbb-ddb8-549e-9671-09418efbb7f5');
    if (!obj) return;

    const cam = this.engine.activeCamera;
    const camPos = cam.cameraPosition;
    const targetPos = cam.targetPosition;

    const forward = vec3.subtract(
      vec3.create(),
      vec3.fromValues(targetPos[0] as number, targetPos[1] as number, targetPos[2] as number),
      vec3.fromValues(camPos[0] as number, camPos[1] as number, camPos[2] as number),
    );
    vec3.normalize(forward, forward);

    const speed = 40 + Math.random() * 20;
    const velocity = vec3.scale(vec3.create(), forward, speed);
    velocity[1] += 3 + Math.random() * 5;

    this.physics.createProjectile({
      position: vec3.fromValues(camPos[0] as number, camPos[1] as number, camPos[2] as number),
      velocity,
      restitution: 0,
      mass: 1,
      maxBounces: 0,
      maxLifetime: 60,
      renderObject: obj,
      enableRotation: false,
      persist: true,
      alignToVelocity: true,
      stickOnImpact: true,
      penetrationFraction: 0.2,
    });
  }

  async switchScene(manifestUrl: string) {
    if (this.loading || manifestUrl === this.activeManifest) return;
    this.activeManifest = manifestUrl;
    await this.loadScene(manifestUrl);
  }

  ngOnDestroy(): void {
    // MsdfFont owns one GL texture (the atlas). The font lives on the App
    // instance rather than the Scene, so Scene.detached() doesn't reach it —
    // release it here so the texture doesn't leak across hot-reloads or
    // route changes that destroy this component. The `destroyed` flag is
    // read by the load `.then()` to handle the race where teardown beats the
    // async atlas decode.
    this.destroyed = true;
    if (this.interFont && this.engine?.ctx) {
      this.interFont.destroy(this.engine.ctx.gl);
      this.interFont = undefined;
    }
    // Closes the AudioContext (and tears down anything else the engine owns).
    // Without this, Angular hot-reloads leak AudioContexts until Chrome's
    // ~6-context cap is hit and audio silently stops working.
    this.engine?.destroy();
  }

  async ngAfterViewInit(): Promise<void> {
    // Scene list lives next to the published scene manifests on the asset
    // server so the dropdown reflects whatever has actually been published.
    // When PLAYFUL_ENV.assetBaseUrl is empty (local dev), the URL collapses to
    // a same-origin `scenes.json` served from playful's `public/`.
    //
    // Awaited before engine setup so `loadScene(this.activeManifest)` below
    // never fires with an empty manifest path — that would resolve to the
    // bare asset base URL and 404 with no CORS headers.
    const assetBaseUrl = (window as any).PLAYFUL_ENV?.assetBaseUrl ?? '';
    const fallbackList: { id: string; name: string; path: string }[] = [];
    const list = await firstValueFrom(
      this.http
        .get<{ id: string; name: string; path: string }[]>(`${assetBaseUrl}scenes.json`)
        .pipe(catchError(() => of(fallbackList))),
    );
    this.sceneList = list;
    if (list.length > 0 && !this.activeManifest) {
      this.activeManifest = list[0].path;
    }

    const settings = await firstValueFrom(this.http.get<EngineSettings>('settings.json'));

    {
      let viewPort = new ViewPort(window.innerWidth, window.innerHeight);

      let contextBuilder = new DOMContextBuilder(
        viewPort,
        this.canvas.nativeElement,
        this.textLayer.nativeElement,
        assetBaseUrl,
      );

      let primaryCamera = new FreeViewCamera(viewPort);
      primaryCamera.name = 'Free View';
      primaryCamera.fov = 70;
      primaryCamera.far = 5000;

      let playerCamera = new AttachedCamera(viewPort);
      playerCamera.name = 'player';
      playerCamera.distance = 4;
      playerCamera.yOffset = 1.6;
      playerCamera.fov = 70;
      playerCamera.far = 5000;

      this.cameras.push(primaryCamera);

      let engine = new Engine(contextBuilder, primaryCamera);
      this.engine = engine;
      // Resolver is rooted at the public/ asset base — `audio/<id>/clip.json`
      // resolves to `/audio/<id>/clip.json` served by the dev server.
      engine.configureAudio({ resolver: new RuntimeAudioResolver('/') });
      engine.audio.resumeOnUserGesture(this.canvas.nativeElement);
      // Preload the default Attack-swing SFX so the first authored
      // `attack_swing` event hits a warm cache. Authored animation-event
      // clips with other ids will warm-load on their first miss; for the
      // canonical slash this preload guarantees a sub-frame fire.
      // TODO: publisher could aggregate all event clipIds into a manifest
      // "audioClipIds" hint and the preload would scan that instead.
      engine.audio.library.acquire('slash').catch((err) => {
        console.warn('[audio] failed to preload slash clip:', err);
      });
      this.sceneLoader = new SceneLoader(engine);

      // Create tile-parse and terrain-gen workers for off-thread tile processing.
      // Both workers are stateless across scene loads (the terrain worker receives
      // a per-scene init message from TileMeshClient.useWorker() each load).
      const tileParseRaw = new Worker(new URL('../tile-parse-shim.worker.ts', import.meta.url), {
        type: 'module',
      });
      this.tileParseWorker = new TileParseWorkerClient(tileParseRaw as unknown as WorkerLike);
      this.terrainGenWorker = new Worker(
        new URL('../terrain-gen-shim.worker.ts', import.meta.url),
        { type: 'module' },
      );

      // Load saved graphics settings from localStorage
      const savedGraphics = GraphicsSettingsPanel.loadSaved();
      if (savedGraphics) {
        engine.graphicsSettings = savedGraphics;
      }

      let inputHandler = new InputHandler(this.canvas.nativeElement);
      inputHandler.hookEvents();
      this.inputHandler = inputHandler;

      const cameraActionBundle = createCameraActions(engine, primaryCamera);
      const cameraActions = cameraActionBundle.actions;
      this.cameraControls = cameraActionBundle.controls;
      let playerActions = createPlayerActions(engine);
      inputHandler.setInputActions(cameraActions);
      let mouseInteraction = new MouseObjectInteraction(engine);

      inputHandler.bindKeyMapping(settings.keymap);
      inputHandler.bindMouseEventAdapter(mouseInteraction);

      engine.tickEventManager.registerFrameEvent(() => inputHandler.handleKeyboard());
      engine.tickEventManager.registerTimeEvent(() => inputHandler.handleMouse(), 50);

      // Pointer lock provider — thin DOM adapter
      const canvas = this.canvas.nativeElement;
      const pointerLock: PointerLockProvider = {
        requestLock: () => canvas.requestPointerLock(),
        exitLock: () => {
          if (document.pointerLockElement === canvas) document.exitPointerLock();
        },
        isLocked: () => document.pointerLockElement === canvas,
      };

      canvas.addEventListener('mousemove', (e: MouseEvent) => {
        if (pointerLock.isLocked()) {
          pointerLock.onMouseDelta?.(e.movementX, e.movementY);
        }
      });
      document.addEventListener('pointerlockchange', () => {
        if (!pointerLock.isLocked() && this.playerController.controlMode === 'player') {
          this.playerController.setControlMode('free-camera');
        }
      });
      canvas.addEventListener('mousedown', () => {
        if (this.playerController.controlMode === 'player' && !pointerLock.isLocked()) {
          pointerLock.requestLock();
        }
      });
      canvas.addEventListener('keydown', (e: KeyboardEvent) => {
        if (e.code === 'Tab') {
          e.preventDefault();
          this.togglePlayerMode();
        }
        if (e.code === 'KeyB') {
          this.debugBoneImpact = !this.debugBoneImpact;
        }
        if (e.code === 'KeyN') {
          // Debug: spawn a wandering Imp at the camera position. Useful for
          // testing dynamic agents (body blocking + local avoidance) and
          // exercising projectile-vs-bone hits on a moving target.
          this.spawnWanderingNpc();
        }
        if (e.code === 'KeyJ') {
          // Debug: dump the floor-query state at the player's (or camera's)
          // current position. Useful for "why is the player falling through
          // the floor visually?" — shows what each Y of the floor-cast finds.
          this.debugFloorQuery();
        }
      });

      // Debug: click to simulate bone impact
      canvas.addEventListener('click', (e: MouseEvent) => {
        if (!this.debugBoneImpact || !this.physics) return;
        const rect = canvas.getBoundingClientRect();
        // Scale CSS pixels to render pixels for getRay
        const scaleX = canvas.width / rect.width;
        const scaleY = canvas.height / rect.height;
        const x = (e.clientX - rect.left) * scaleX;
        const y = canvas.height - (e.clientY - rect.top) * scaleY; // flip Y for GL
        const ray = engine.activeCamera.getRay(x, y);
        const hit = this.physics.boneHitDetection.testRay(ray);
        if (hit) {
          this.physics.boneImpulseSystem.applyImpulse(
            hit.target.renderObject,
            hit.boneCapsule.jointIndex,
            ray.direction,
            0.8,
          );

          // Floating damage number at the hit point. Self-expires on its
          // own; OverlayManager reaps it without a callback here.
          if (this.interFont && this.scene) {
            const damage = Math.floor(20 + Math.random() * 80);
            const dn = new DamageNumberOverlay({
              font: this.interFont,
              text: damage.toString(),
              fontSize: 22,
              color: [1, 0.45, 0.35, 1],
              align: 'center',
              screenOffsetY: 0,
            });
            dn.worldPosition[0] = hit.hitPoint[0];
            dn.worldPosition[1] = hit.hitPoint[1];
            dn.worldPosition[2] = hit.hitPoint[2];
            this.scene.overlayManager.add(dn);
          }
        }
      });

      // Create player controller
      this.playerController = new PlayerController({
        engine,
        inputHandler,
        playerCamera,
        freeCamera: primaryCamera,
        cameraActions,
        playerActions,
        pointerLock,
        onAttack: () => {
          if (!this.physics || !this.playerObject) return;
          this.physics.meleeAttackSystem.triggerAttack(this.playerObject, {
            range: 1.6,
            angle: Math.PI / 2, // 90° cone
            power: 0.9,
            hitDelay: 0.3,
          });
          // The swing sound is now an authored Attack-animation event with
          // its `clipId` set (see the asset-manager animation viewer); the
          // generic `onAnyEvent` subscription in `wireFootsteps` picks it up.
        },
      });

      await this.loadScene(this.activeManifest);

      // Handle window resize with debounce to avoid thrashing FBO allocations
      let resizeTimer: ReturnType<typeof setTimeout> | undefined;
      window.addEventListener('resize', () => {
        const w = window.innerWidth;
        const h = window.innerHeight;
        const scale = this.deferredRenderer?.graphicsSettings.renderScale ?? 1.0;
        contextBuilder.resizeCanvas(w, h, scale);
        for (const cam of this.cameras) {
          (cam as any).invalidateProjection?.();
        }
        clearTimeout(resizeTimer);
        resizeTimer = setTimeout(() => engine.resize(), 150);
      });

      engine.start();

      // Preload the overlay font so the bone-impact click handler can spawn
      // damage numbers synchronously. Fire-and-forget; if the click lands
      // before the load finishes we just skip that one damage number. If
      // the component dies while the load is in flight, destroy the atlas
      // here instead of orphaning it on a dead App instance.
      this.interFontPromise = MsdfFont.load(engine.ctx, interRegular).then((font) => {
        if (this.destroyed) {
          font.destroy(engine.ctx.gl);
          return font;
        }
        this.interFont = font;
        return font;
      });
    }
  }

  private findFirstAnimatedObject(
    namedObjects: Map<string, RenderObject[]>,
  ): RenderObject | undefined {
    for (const [, objects] of namedObjects) {
      for (const obj of objects) {
        if (obj.isAnimated) return obj;
      }
    }
    return undefined;
  }

  /**
   * Load an animated character by model UUID and instantiate it at
   * `spawnPoint`. Caches the model in `scene.modelRegistry` so subsequent
   * spawns of the same model skip the GLB fetch.
   *
   * Wires up:
   *  - animation state + transform from `_MODEL_TRANSFORM` GLB extras
   *  - bone hit detection (so projectiles can target individual bones)
   *  - dynamic-agent registration (so it body-blocks other agents)
   *
   * Caller is responsible for any nav-aware behaviour binding (player
   * control, NPC AI). Idempotent w.r.t. agent registration — wiring through
   * `Scene.createNavigationAwareObject` later will reuse the same Agent.
   */
  private async loadAnimatedCharacter(
    scene: Scene,
    modelId: string,
    meshTag: string,
    spawnPoint?: number[],
  ): Promise<RenderObject | null> {
    if (!scene.modelRegistry.has(modelId)) {
      try {
        const compressed = await scene.ctx.resourceLoader.loadResource(`models/${modelId}.glb.gz`);
        const glbData = await inflateGzip(compressed);
        const parser = new GltfParser(glbData);
        const parsed = parser.parse();
        const resolvedTextures = parser.resolveTextures(parsed.json, parsed.binaries);
        const materials = await resolveMaterialTextures(scene.ctx, resolvedTextures);
        const meshes = parser.resolveSceneMeshes(parsed.json, parsed.binaries);
        const rawMeshes = parser.resolveAllMeshes(parsed.json, parsed.binaries);
        const model: ParsedModel = {
          parser,
          meshes,
          rawMeshes,
          meshByName: new Map(meshes.map((m) => [m.name, m])),
          rawMeshByName: new Map(rawMeshes.map((m) => [m.name, m])),
          materials,
          lightmapTextures: [],
          json: parsed.json,
          binaries: parsed.binaries,
        };
        scene.modelRegistry.set(modelId, model);
      } catch (e) {
        console.warn(`Failed to load model ${modelId}`, e);
        return null;
      }
    }

    const model = scene.modelRegistry.get(modelId)!;
    const raw = model.rawMeshes[0];
    if (!raw || raw.primitives.length === 0) return null;

    const prim = raw.primitives[0];
    const mesh = new GltfMesh(meshTag, scene.ctx, scene, prim);

    const anim = new GltfAnimation(model.json, model.binaries);
    anim.preComputeAnimations();
    mesh.animationDataTexture = new GltfAnimationDataTexture(scene.ctx, anim);
    mesh.animation = anim;

    const obj = scene.objectFactory.instantiateMesh(mesh);
    if (prim.materialIndex !== undefined) {
      const mat = model.materials.get(prim.materialIndex);
      if (mat) obj.material = mat;
    }

    const modelTransform = readModelTransform(model.json);
    if (modelTransform?.scale !== undefined) obj.trs.scale = modelTransform.scale;
    if (modelTransform?.rotation) {
      obj.trs.setRotation(
        modelTransform.rotation[0],
        modelTransform.rotation[1],
        modelTransform.rotation[2],
      );
    }

    if (spawnPoint) {
      obj.trs.position = spawnPoint;
    }

    obj.animationState = createAnimationState(obj, model.json);
    obj.animationState.switchState('Idle');

    // Wire footsteps for every animated character. Per-surface clip resolution
    // happens at fire time so the same animated entity gets different sounds
    // on grass vs. stone. NPCs without surface coverage just play nothing.
    this.wireFootsteps(scene, obj);

    const physics = scene.features.get('physics') as PhysicsFeature | undefined;
    physics?.boneHitDetection.registerAnimated(obj, model.json);
    // Body blocking: register so other agents (player, other NPCs) can't
    // walk through this entity. Idempotent w.r.t. `createNavigationAwareObject`
    // — both paths converge on a single Agent per RenderObject.
    scene.agentRegistry.registerAnimated(obj);

    return obj;
  }

  private async loadMutantPlayer(
    scene: Scene,
    spawnPoint?: number[],
  ): Promise<RenderObject | null> {
    return this.loadAnimatedCharacter(
      scene,
      '443ec156-200c-585a-94c9-066dae591cab',
      'mutant_player',
      spawnPoint,
    );
  }

  /**
   * Subscribe animation events to the engine's audio system. Two routes:
   *
   *  1. `foot_down`: resolve the surface under the entity at fire time so a
   *     single Walk cycle plays different clips as the character crosses
   *     surface boundaries. Surface-driven; ignores any `event.clipId` on
   *     the event itself.
   *  2. **Generic clipId play**: any event with `event.clipId` set plays
   *     that clip at the entity's position. Lets authors drop a swing
   *     sound on an Attack animation event without writing a per-name
   *     handler here.
   *
   * Both are sync via `playOneShot` and silently drop if the clip isn't
   * cached yet (preloading happens in {@link preloadAudio}).
   */
  private wireFootsteps(scene: Scene, obj: RenderObject): void {
    const animState = obj.animationState;
    if (!animState) return;
    animState.onEvent('foot_down', () => {
      const streamer = scene.terrainStreamer;
      if (!streamer) return;
      const pos = obj.position;
      const clipId = streamer.getFootstepClipAt(pos[0], pos[2]);
      if (!clipId) return;
      this.engine.audio.playOneShot(clipId, { position: [pos[0], pos[1], pos[2]] });
    });
    animState.onAnyEvent((event) => {
      if (!event.clipId) return;
      // Footstep events resolve their clip from the surface (above), not from
      // the event payload — skip the generic route so the author's surface
      // wiring stays authoritative.
      if (event.name === 'foot_down') return;
      const pos = obj.position;
      this.engine.audio.playOneShot(event.clipId, { position: [pos[0], pos[1], pos[2]] });
    });
  }

  /**
   * Acquire every audio clip the manifest references — terrain surface
   * footstep clips and composite ambient-sound slots — so the runtime fire
   * path hits a warm cache. Refcount + single-flight in the library means
   * repeated ids are coalesced. Awaits all acquires (success OR failure)
   * so the caller can safely patch composites afterward.
   */
  private async preloadAudio(manifest: SceneManifest): Promise<void> {
    const ids = new Set<string>();
    for (const s of manifest.terrain?.surfaces ?? []) {
      if (s.footstepClipId) ids.add(s.footstepClipId);
    }
    for (const def of Object.values(manifest.compositeDefs ?? {})) {
      const clipId = def.slots.ambientSound?.clipId;
      if (clipId) ids.add(clipId);
    }
    for (const zone of manifest.ambientZones ?? []) {
      if (zone.clipId) ids.add(zone.clipId);
    }
    // Sound emitter clips (manifest-globals + tile-streamed) are acquired by
    // the engine's own load paths (SceneManifestLoader + TiledObjectStreamer)
    // so they don't need to be enumerated here. Doing so would double-acquire
    // and leak refs on the playful side.
    await Promise.all(
      [...ids].map((id) =>
        this.engine.audio.library.acquire(id).catch((err) => {
          console.warn(`[audio] preload failed for clip ${id}:`, err);
        }),
      ),
    );
  }

  /**
   * After preload completes, walk every composite that declared an ambient
   * sound and create its AudioSource. Composites instantiated before the
   * clips finished decoding would otherwise stay silent — the slot is set
   * but the source pointer is null. Uses the canonical placement→world
   * helper so rotated/scaled placements emit from the same spot they
   * would have at construction time.
   */
  private attachLateAmbientSources(scene: Scene, manifest: SceneManifest): void {
    const placements = new Map<string, SceneCompositeRef>();
    for (const p of manifest.composites ?? []) placements.set(p.instanceId, p);

    for (const composite of scene.compositeInstances.values()) {
      if (composite.ambientSound) continue;
      const slot = composite.def.slots.ambientSound;
      if (!slot) continue;
      const ref = placements.get(composite.instanceId);
      if (!ref) continue; // tile-streamed composites instantiate after preload; skip
      const source = this.engine.audio.createSourceFromCached(slot.clipId, {
        bus: 'ambient',
        volume: slot.volume ?? 1,
        loop: true,
        position: composeAmbientSoundWorldPosition(manifestPlacementToEngine(ref), slot),
      });
      if (source) composite.attachAmbientSound(source);
    }
  }

  /**
   * Debug: spawn a wandering Imp NPC at the camera's current XZ. The Imp gets
   * a `NavigationAwareObject`, an `EntityMovementBehaviour` (handles animation
   * + facing direction during walking), and an `EntityWanderingBehaviour`
   * (picks random reachable nav nodes within range every second). Also
   * exercises body blocking — the player can't walk through the Imp, the
   * Imp can't walk through the player or other spawned Imps.
   *
   * Triggered by the `KeyN` shortcut. Useful for testing the agent registry,
   * the local-avoidance step revert, and projectile-vs-bone collision in
   * an animated context.
   */
  /**
   * Diagnostic: at the player's current XZ (or camera XZ if no player),
   * sweeps a floor query through multiple Y offsets and reports what hits
   * at each. If the player is "falling through" the upper level, this shows
   * whether the spatial grid actually has the upper-level geometry or only
   * the terrain beneath. The query Y values mirror the ones the runtime
   * uses (`currentY + maxStep`, `node.max.y`, etc).
   */
  debugFloorQuery(): void {
    const scene = this.scene;
    if (!scene) return;
    const grid = scene.spatialGrid;
    if (!grid) {
      console.log('[floor-query] no spatialGrid on scene');
      return;
    }
    const player = this.playerObject;
    const pos: [number, number, number] = player
      ? [player.position[0] as number, player.position[1] as number, player.position[2] as number]
      : [
          this.engine.activeCamera.cameraPosition[0] as number,
          this.engine.activeCamera.cameraPosition[1] as number,
          this.engine.activeCamera.cameraPosition[2] as number,
        ];
    const queries: Array<[string, number]> = [
      ['playerY+0.6 (maxStep)', pos[1] + 0.6],
      ['playerY+2', pos[1] + 2],
      ['playerY+10', pos[1] + 10],
      ['playerY+50', pos[1] + 50],
      ['bbox.maxY+100', (scene.boundingBox?.max[1] ?? 1000) + 100],
    ];
    console.log(
      `[floor-query] pos=(${pos[0].toFixed(2)}, ${pos[1].toFixed(2)}, ${pos[2].toFixed(2)})`,
    );
    console.log(
      `[floor-query] hasTileAt(${pos[0].toFixed(2)}, ${pos[2].toFixed(2)})=${grid.hasTileAt(pos[0], pos[2])}`,
    );
    for (const [label, y] of queries) {
      const floor = grid.getFloorHeightBelow(pos[0], y, pos[2]);
      const tagged = grid.getFloorHeightBelowTagged(pos[0], y, pos[2]);
      console.log(
        `[floor-query]   from Y=${y.toFixed(2)} (${label}) → floor=${floor?.toFixed(3) ?? 'null'} terrainOrigin=${tagged?.terrainOrigin ?? 'n/a'}`,
      );
    }
    // Footprint helper — show each of the 5 samples plus what the helper
    // picks, so we can see whether the surface ABOVE the player (e.g. a thin
    // board you're standing on) is being filtered out or just missed.
    const navObj = this.playerController.navObject;
    const radius = navObj?.navigationData.selfAgent?.radius ?? 0;
    const maxStep = navObj?.navigationData.navigation.maxNeighbourStepHeight ?? 0.5;
    const originY = pos[1] + maxStep;
    const offsets: [string, number, number][] = [
      ['center', 0, 0],
      ['+x', radius, 0],
      ['-x', -radius, 0],
      ['+z', 0, radius],
      ['-z', 0, -radius],
    ];
    console.log(
      `[floor-query] footprint samples (radius=${radius.toFixed(3)} maxStep=${maxStep.toFixed(2)} originY=${originY.toFixed(2)}):`,
    );
    for (const [label, dx, dz] of offsets) {
      const sx = pos[0] + dx;
      const sz = pos[2] + dz;
      const s = grid.getFloorHeightBelow(sx, originY, sz);
      const inRange = s !== null && s !== undefined && s - pos[1] <= maxStep;
      console.log(
        `[floor-query]   ${label} (${sx.toFixed(2)},${sz.toFixed(2)}) → ${s?.toFixed(3) ?? 'null'} delta=${s !== null && s !== undefined ? (s - pos[1]).toFixed(3) : 'n/a'} inRange=${inRange}`,
      );
    }
    const helperResult = sampleFootprintFloorHeight(grid, pos[0], pos[1], pos[2], radius, maxStep);
    console.log(`[floor-query] helper picks → ${helperResult?.toFixed(3) ?? 'null'}`);
    // Show the body's current physics state — what we ACTUALLY pin to.
    const body = navObj?.physicsBody;
    if (body) {
      console.log(
        `[floor-query] physicsBody: surfaceHeight=${body.surfaceHeight?.toFixed(3) ?? 'null'} minFloorHeight=${body.minFloorHeight?.toFixed(3) ?? 'null'} grounded=${body.grounded} velY=${body.velocity[1].toFixed(3)}`,
      );
    }
    // Also: nav state. Which path node is the player in?
    if (navObj?.movement.currentNode) {
      const node = navObj.movement.currentNode.node;
      const bb = node.getBoundingBox();
      console.log(
        `[floor-query] currentNode bb=[${bb.min[0].toFixed(1)},${bb.min[1].toFixed(1)},${bb.min[2].toFixed(1)} → ${bb.max[0].toFixed(1)},${bb.max[1].toFixed(1)},${bb.max[2].toFixed(1)}]`,
      );
    } else {
      console.log('[floor-query] no currentNode on player');
    }
  }

  async spawnWanderingNpc(): Promise<void> {
    const scene = this.scene;
    if (!scene) return;
    const cam = this.engine.activeCamera;
    const pos = cam.cameraPosition;
    const cx = pos[0] as number;
    const cy = pos[1] as number;
    const cz = pos[2] as number;

    let spawnY = cy;
    if (scene.spatialGrid) {
      await scene.spatialGrid.loadInitialTiles(cx, cz);
      let floor = scene.spatialGrid.getFloorHeightBelow(cx, cy + 50, cz);
      if (floor === null) {
        const bbMaxY = scene.boundingBox?.max[1] ?? 10000;
        floor = scene.spatialGrid.getFloorHeightBelow(cx, bbMaxY + 100, cz);
      }
      if (floor !== null) spawnY = floor;
    }
    if (scene.navMeshStreamer) {
      await scene.navMeshStreamer.loadTilesAround(cx, cz);
    }

    const impId = '5dc7acf7-1841-50ac-9ab1-ff357341952d';
    const obj = await this.loadAnimatedCharacter(
      scene,
      impId,
      `imp_npc_${performance.now().toFixed(0)}`,
      [cx, spawnY, cz],
    );
    if (!obj) return;

    // 2 m/s base speed; the wandering behaviour picks fresh destinations
    // within 15m of the current node, with a ~30% chance per second of
    // starting a new walk when idle. Tweak per playtest.
    const navObject = scene.createNavigationAwareObject(obj, 2);
    navObject.bind(new EntityMovementBehaviour(obj));
    navObject.bind(new EntityWanderingBehaviour(scene, 0.3, 15));

    // Anchor the nameplate above the imp's head using the local bounding-box
    // height scaled by the runtime TRS — same derivation that AgentRegistry
    // uses for body sizing. Tracker's `position` is a single cached vec3 that
    // we re-stamp in the per-frame tick below; the overlay manager reads it
    // (and resolvePosition reads it again on emit) so a getter that allocates
    // would land in the per-frame hot path.
    const localHeight =
      ((obj.bounds?.localMax?.[1] ?? 1.5) - (obj.bounds?.localMin?.[1] ?? 0)) *
      (obj.trs?.scale ?? 1);
    const headOffset = vec3.fromValues(0, localHeight + 0.3, 0);
    const trackerPos = vec3.create();
    const tracker = { position: trackerPos };
    // Bundle the imp's nameplate (label + two bars) under a CompositeOverlay
    // so the three children share a tracker and can be added/removed in one
    // shot. Two bars exercise the depth-tested vs always-visible modes; the
    // label exercises the MSDF text path.
    const font = await (this.interFontPromise ?? Promise.resolve(this.interFont!));
    const nameplate = new CompositeOverlay();
    nameplate.setTracker(tracker);

    const barAlwaysOn = nameplate.add(
      new BarOverlay({
        size: [70, 7],
        screenOffsetY: -6,
        fill: 1,
        depthTested: false,
      }),
    );
    const barOccluded = nameplate.add(
      new BarOverlay({
        size: [70, 7],
        screenOffsetY: 6,
        fill: 1,
        fillColor: [0.25, 0.55, 0.95, 1],
        depthTested: true,
      }),
    );
    nameplate.add(
      new TextOverlay({
        font,
        text: 'Imp',
        fontSize: 14,
        color: [1, 1, 1, 1],
        align: 'center',
        screenOffsetY: -22,
        depthTested: false,
      }),
    );
    scene.overlayManager.add(nameplate);

    // Per-frame: re-stamp trackerPos = obj.position + headOffset (no alloc),
    // and run the test-bleed animation that exercises the bar fill + color
    // ramp. The three color tuples are hoisted so the closure swaps a
    // reference each frame instead of allocating a fresh literal.
    const COLOR_GREEN: [number, number, number, number] = [0.2, 0.85, 0.25, 1];
    const COLOR_YELLOW: [number, number, number, number] = [0.95, 0.85, 0.15, 1];
    const COLOR_RED: [number, number, number, number] = [0.95, 0.18, 0.18, 1];
    let bleed = 1;
    scene.tickEventManager.registerFrameEvent(() => {
      vec3.add(trackerPos, obj.position as vec3, headOffset);
      bleed -= 1 / (60 * 30);
      if (bleed < 0) bleed = 1;
      barAlwaysOn.fill = bleed;
      barOccluded.fill = bleed;
      barAlwaysOn.fillColor = bleed > 0.5 ? COLOR_GREEN : bleed > 0.2 ? COLOR_YELLOW : COLOR_RED;
    });

    console.log(`[npc] spawned imp at (${cx.toFixed(1)}, ${spawnY.toFixed(1)}, ${cz.toFixed(1)})`);
  }

  async togglePlayerMode(): Promise<void> {
    if (this.playerController.controlMode === 'player') {
      this.playerController.setControlMode('free-camera');
      return;
    }

    const scene = this.scene;
    if (!scene) return;

    if (!this.playerSetupDone) {
      const cam = this.engine.activeCamera;
      const pos = cam.cameraPosition;
      const cx = pos[0] as number;
      const cy = pos[1] as number;
      const cz = pos[2] as number;

      let spawnY = cy;
      if (scene.spatialGrid) {
        await scene.spatialGrid.loadInitialTiles(cx, cz);
        // Search for a floor directly under the camera first; if that misses
        // (camera flew below ground), search from well above the scene so we
        // find the topmost surface at this XZ. Common when the user freecams
        // into a placed building from below and then snaps into player mode.
        let floor = scene.spatialGrid.getFloorHeightBelow(cx, cy + 50, cz);
        if (floor === null) {
          const bbMaxY = scene.boundingBox?.max[1] ?? 10000;
          floor = scene.spatialGrid.getFloorHeightBelow(cx, bbMaxY + 100, cz);
        }
        if (floor !== null) spawnY = floor;
      }
      // The navmesh streamer is otherwise only driven by the player position
      // (see the tickEventManager registration below) — when the user freecams
      // to a fresh region and snaps into player mode, no nav tiles are loaded
      // there yet, so the spawn nav-node lookup misses. Load them here before
      // constructing the player. This must run AFTER the spatialGrid floor
      // snap so we load tiles around the actual spawn position, not the
      // freecam Y.
      if (scene.navMeshStreamer) {
        await scene.navMeshStreamer.loadTilesAround(cx, cz);
      }

      const obj = await this.loadMutantPlayer(scene, [cx, spawnY, cz]);
      if (obj) {
        this.playerObject = obj;
        this.playerController.setupEntity(scene, obj);
        this.playerController.registerFrameEvents(scene);
        this.playerSetupDone = true;
      }
    }

    if (this.playerObject) {
      this.playerController.setControlMode('player');
    }
  }

  private async loadScene(manifestUrl: string) {
    this.loading = true;

    this.playerController.teardown();

    // Clear app-level scene state
    this.stressTestLights = undefined;
    this.boxMesh = undefined;
    this.physics = undefined;
    this.playerObject = undefined;
    this.playerSetupDone = false;
    this.ambientZoneController?.destroy();
    this.ambientZoneController = undefined;

    // Subscribe to loading progress for the overlay
    this.progressSub?.unsubscribe();
    this.progressSub = this.sceneLoader.progress$.subscribe((event) => {
      this.loadingProgress = event;
    });

    const inputHandler = this.inputHandler;

    try {
      const { scene, manifest, namedObjects } = await this.sceneLoader.loadScene(() => {
        const s = new Scene(this.engine, inputHandler);
        s.debugManager.enable('fps');
        if (this.tileParseWorker) s.tileParseWorker = this.tileParseWorker;
        if (this.terrainGenWorker)
          s.terrainGenWorker = this.terrainGenWorker as unknown as WorkerLike;

        return s;
      }, manifestUrl);

      this.scene = scene;

      // Warm up the audio library for every clip the manifest references —
      // surface footstep clips and composite ambient-sound slots. Awaited so
      // we can patch ambient loops onto composites whose clips weren't
      // cached at construction time. preloadAudio returns when every
      // referenced clip has resolved (success or failure).
      await this.preloadAudio(manifest);
      this.attachLateAmbientSources(scene, manifest);
      // Controller cleared by the loadScene preamble; no need to destroy again.
      this.ambientZoneController = new AmbientZoneController(
        this.engine.audio,
        manifest.ambientZones ?? [],
      );

      // Drop the orbit center on the terrain at the world origin so the eye
      // floats above the surface instead of clipping through it. (Scene
      // manifests carry a `spawnPoint` field but it's used for the player
      // object, not the camera, and isn't authored yet — so we ignore it
      // here and just sample terrain at origin.)
      if (this.cameras.length > 0) {
        const terrainY = scene.terrainStreamer?.getHeightField()?.sampleHeight(0, 0) ?? 0;
        for (const cam of this.cameras) {
          if ('x' in cam) {
            (cam as any).x = 0;
            (cam as any).y = terrainY;
            (cam as any).z = 0;
          }
        }
      }

      // Physics setup (if physics feature is present). Bone hit detection
      // registration for animated objects is handled by SceneManifestLoader.
      this.physics = scene.features.get('physics') as PhysicsFeature | undefined;
      if (this.physics) {
        this.boxMesh = GeometryMesh.fromCollection('physics-box', scene, createCube(1));
        // Impact SFX hook — fires for both projectile and melee bone hits.
        // The victim model's `_HIT_CLIP` extras drives the clip; null = silent.
        this.physics.onBoneHit = (hit) => {
          const clipId = hit.target.hitClipId;
          if (!clipId) return;
          this.engine.audio.playOneShot(clipId, {
            position: [hit.hitPoint[0], hit.hitPoint[1], hit.hitPoint[2]],
          });
        };
      }

      // Drive navmesh streamer from player position only — not from the free-view camera.
      // The navmesh exists for NPC pathfinding; loading tiles around a flying camera
      // wastes CPU and causes constant tile churn.
      scene.tickEventManager.registerFrameEvent(() => {
        const player = this.playerObject;
        if (player && scene.navMeshStreamer) {
          const pos = player.position;
          scene.navMeshStreamer.updateCamera(pos[0], pos[2]);
        }
      });

      // Stress test lights
      const spread = { x: 50, yCenter: 30, y: 30, z: 25 };
      this.stressTestLights = new StressTestLights(scene, spread);
      scene.tickEventManager.registerFrameEvent(() => {
        this.stressTestLights?.update(performance.now());
      });

      // Ambient zone polygons: tick on the listener position (active camera
      // world XZ) so biome beds crossfade as the player walks across regions.
      scene.tickEventManager.registerFrameEvent(() => {
        const ctrl = this.ambientZoneController;
        if (!ctrl) return;
        const camPos = this.engine.activeCamera.cameraPosition;
        ctrl.tick(camPos[0], camPos[1], camPos[2]);
      });
    } finally {
      this.progressSub?.unsubscribe();
      this.loadingProgress = null;
      this.loading = false;
    }
  }
}
